<template>
  <div class="table-responsive-lg">

    <download-json-to-csv
                class="orderdetails__download-json"
                :data="orderDetailsForDownload"
                :file-name="getFilename"
              >
                <button
                  class="btn backorders__download-button"
                >
                  Download CSV
                </button>
    </download-json-to-csv>
    <table class="table table-striped blue return-detail-items items-with-sub-lines">
      <thead>
        <tr>
          <th>{{ getContentItem("Line") }}</th>
          <th>{{ getContentItem("PartNumber") }}</th>
          <th>{{ getContentItem("description") }}</th>

          <th v-if="details.PartsStoreCustomerNumber">{{ getContentItem("partsStoreOrderHashSign") }}</th>

          <template v-if="details.isMcfOrder">
            <th>{{ getContentItem("status") }}</th>
            <th>{{ getContentItem("Warehouse") }}</th>
            <th>{{ getContentItem("Location") }}</th>
          </template>

          <th>{{ getContentItem("PromisedDate") }}</th>
          <th>{{ getContentItem("Ordered") }}</th>
          <th>{{ getContentItem(details.isMcfOrder ? "released" : "Shipped") }}</th>
          <th>{{ getContentItem("QuantityBackordered") }}</th>

          <template v-if="details.showPrices">
            <th>{{ getContentItem("UnitPrice") }}</th>
            <th>{{ getContentItem("ExtPrice") }}</th>
          </template>
        </tr>
      </thead>
      <tbody>
        <template v-for="(item, index) in details.Items">
          <tr :key="index" class="sub-line">
            <template v-if="item.LineType === 'Part'">
              <td>{{ item.LineNumber }}</td>
              <td>
                <part-details-link :oem-part-code="item.OemPartCode" />
              </td>
              <td>{{ item.Description }}</td>

              <td v-if="details.PartsStoreCustomerNumber">{{ item.PartStoreOrderNumber }}</td>

              <template v-if="details.isMcfOrder">
                <td>{{ getLineStatus(item.LineStatus) }}</td>
                <td>{{ item.SourceWarehouseCode }}</td>
                <td>{{ item.StockingLocation }}</td>
              </template>

              <td>{{ formatDateTime(item.PromisedShipDate) }}</td>
              <td>{{ item.QuantityOrdered }}</td>
              <td>{{ item.QuantityReleased }}</td>
              <td>{{ item.QuantityBackordered }}</td>

              <template v-if="details.showPrices">
                <td>
                  <currency-text
                    :amount="item.UnitPrice"
                    :currency-code="details.CurrencyCode"
                  />
                </td>
                <td>
                  <currency-text
                    :amount="item.ExtendedPrice"
                    :currency-code="details.CurrencyCode"
                  />
                </td>
              </template>
            </template>
            <template v-if="item.LineType === 'Comment'">
              <td class="right muted">
                {{ item.LineNumber }}
              </td>
              <td class="muted">
                <em>Comment</em>
              </td>
              <td colspan="100%" class="muted">
                {{ item.Description }} {{ item.Message }}
              </td>
            </template>
          </tr>
        </template>
      </tbody>
      <tfoot v-if="details.showPrices">
        <template v-if="details.TotalCorePrice">
          <tr class="row-fee">
            <td colspan="100%">
              <b-button v-b-toggle.core-items variant="link">
                Details
              </b-button>
              <label for="corecost" class="inline-label">Core Costs:</label>
              <currency-text
                id="corecost"
                :amount="details.TotalCorePrice"
                :currency-code="details.CurrencyCode"
              />
              <b-collapse id="core-items">
                <div v-for="(item, index) in details.CoreItems" :key="index" class="row-sub-fee">
                  <label :for="'CorePrice' + index" class="inline-label">{{ item.OemPartCode }}</label>
                  <currency-text
                    :id="'CorePrice' + index"
                    :amount="item.CorePrice"
                    :currency-code="details.CurrencyCode"
                  />
                </div>
              </b-collapse>
            </td>
          </tr>
        </template>
        <template v-if="details.TotalEmergencyFee">
          <tr class="row-fee">
            <td colspan="100%">
              <b-button v-b-toggle.emergency-fee-items variant="link">
                Details
              </b-button>
              <label for="corecost" class="inline-label">Emergency Fees:</label>
              <currency-text
                id="corecost"
                :amount="details.TotalEmergencyFee"
                :currency-code="details.CurrencyCode"
              />
              <b-collapse id="emergency-fee-items">
                <div
                  v-for="(item, index) in details.EmergencyFeeItems"
                  :key="index"
                  class="row-sub-fee"
                >
                  <label :for="'EmergencyFee' + index" class="inline-label">{{ item.OemPartCode }}</label>
                  <currency-text
                    :id="'EmergencyFee' + index"
                    :amount="item.EmergencyFee"
                    :currency-code="details.CurrencyCode"
                  />
                </div>
              </b-collapse>
            </td>
          </tr>
        </template>
        <template v-if="details.TotalFreight">
          <tr class="row-fee">
            <td colspan="100%">
              <b-button v-b-toggle.freight-items variant="link">
                Details
              </b-button>
              <label for="corecost" class="inline-label">Freight Charges:</label>
              <currency-text
                id="corecost"
                :amount="details.TotalFreight"
                :currency-code="details.CurrencyCode"
              />
              <b-collapse id="freight-items">
                <div v-for="(item, index) in details.FreightItems" :key="index" class="row-sub-fee">
                  <label :for="'Freight' + index" class="inline-label">{{ item.OemPartCode }}</label>
                  <currency-text
                    :id="'Freight' + index"
                    :amount="item.Freight"
                    :currency-code="details.CurrencyCode"
                  />
                </div>
              </b-collapse>
            </td>
          </tr>
        </template>
        <template v-if="details.TotalInsurance">
          <tr class="row-fee">
            <td colspan="100%">
              <b-button v-b-toggle.insurance-items variant="link">
                Details
              </b-button>
              <label for="corecost" class="inline-label">Insurance Charges:</label>
              <currency-text
                id="corecost"
                :amount="details.TotalInsurance"
                :currency-code="details.CurrencyCode"
              />
              <b-collapse id="insurance-items">
                <div
                  v-for="(item, index) in details.InsuranceItems"
                  :key="index"
                  class="row-sub-fee"
                >
                  <label :for="'Insurance' + index" class="inline-label">{{ item.OemPartCode }}</label>
                  <currency-text
                    :id="'Insurance' + index"
                    :amount="item.Insurance"
                    :currency-code="details.CurrencyCode"
                  />
                </div>
              </b-collapse>
            </td>
          </tr>
        </template>
        <template v-if="details.TotalOtherFees">
          <tr class="row-fee">
            <td colspan="100%">
              <b-button v-b-toggle.other-fee-items variant="link">
                Details
              </b-button>
              <label for="corecost" class="inline-label">Other Fees:</label>
              <currency-text
                id="corecost"
                :amount="details.TotalOtherFees"
                :currency-code="details.CurrencyCode"
              />
              <b-collapse id="other-fee-items">
                <div
                  v-for="(item, index) in details.OtherFeeItems"
                  :key="index"
                  class="row-sub-fee"
                >
                  <label :for="'OtherFees' + index" class="inline-label">{{ item.OemPartCode }}</label>
                  <currency-text
                    :id="'OtherFees' + index"
                    :amount="item.OtherFees"
                    :currency-code="details.CurrencyCode"
                  />
                </div>
              </b-collapse>
            </td>
          </tr>
        </template>
        <tr v-show="details.showSubtotal" class="row-sub-total">
          <td colspan="100%">
            <label for="subtotal" class="inline-label">{{ getContentItem("subtotal") }}:</label>
            <currency-text
              id="subtotal"
              :amount="details.SubTotal"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
  import { mapGetters }                    from "vuex";
  import { CurrencyText, PartDetailsLink } from "@/components/Elements";
  import { formatDateTime}                 from "@/components/mixins";
  import { DownloadJsonToCsv } from "@/components/Elements/DownloadJsonToCsv";

  export default {
    name: "OrderDetailsGrid",
    components: {
      CurrencyText,
      PartDetailsLink,
      DownloadJsonToCsv
    },
    mixins: [formatDateTime],
    computed: {
      ...mapGetters(["getContentItem", "hasPermission"]),
      getFilename() {
        var dateString = this.getDateString();
        var orderInvoiceNumber = (typeof this.details.Id != 'undefined' ? this.details.Id : (typeof this.details.OrderNumber != 'undefined') ? this.details.OrderNumber : "");
        return `Order_` + orderInvoiceNumber + `_${dateString}.csv`;
      },
      orderDetailsForDownload(){
           var self = this;
            var orderDetailsForDownload = this.details.Items.map(function(x) {
              return {
                LineNumber: x.LineNumber,
                OemPartCode: x.OemPartCode,
                Description: x.Description,
                PartStoreOrderNumber: x.PartStoreOrderNumber,
                Status: x.LineStatus,
                Warehouse: x.SourceWarehouseCode,
                Location: x.StockingLocation,
                PromisedDate: x.PromisedShipDate,
                Ordered: x.QuantityOrdered,
                Released: x.QuantityReleased,
                QuantityBackordered: x.QuantityBackordered,
                UnitPrice: !self.hasPermission("HideInvoicePricing") ? x.UnitPrice : "",
                ExtendedPrice: !self.hasPermission("HideInvoicePricing") ?  x.ExtendedPrice : ""
              }
            });

            return orderDetailsForDownload;
        }

    },
    props: {
      details: Object
    },
    methods: {
      getLineStatus(status){
        if(status === "NONE")
        {
          return "";
        }
        return status;
      },     
      getDateString() {
        const date = new Date();
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const hour = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();
        const day =`${date.getDate()}`.padStart(2, '0');
        return `${year}${month}${day}_${hour}${minutes}${seconds}`
      },
    }
  };
</script>
